import { useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { useSeo } from "@/hooks/useSeo";
import { animeRoles } from "@/mocks/roles";
import RoleCard from "@/components/base/RoleCard";
import EmptyState from "@/components/base/EmptyState";
import Pagination from "@/components/base/Pagination";
import Breadcrumb from "@/components/base/Breadcrumb";
import FilterBar from "@/pages/roles/components/FilterBar";

const PAGE_SIZE = 8;

export default function Roles() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [sort, setSort] = useState<"popularity" | "newest">("popularity");
  const [page, setPage] = useState(1);

  useSeo({
    title: "萝莉角色分类 - 萝莉动漫主题站",
    description:
      "萝莉角色分类页支持按类型、属性、声优及是否新角色多维筛选，并提供人气与发布时间排序，帮你快速找到心仪的二次元萝莉角色。",
    keywords: "萝莉角色,角色分类,动漫角色筛选,萝莉角色库,二次元角色"
  });

  const keyword = searchParams.get("q") ?? "";
  const tag = searchParams.get("tag") ?? "";
  const attr = searchParams.get("attr") ?? "";
  const cv = searchParams.get("cv") ?? "";
  const onlyNew = searchParams.get("new") === "1";

  const update = (key: string, value: string) => {
    const next = new URLSearchParams(searchParams);
    if (value) next.set(key, value);
    else next.delete(key);
    setSearchParams(next, { replace: true });
    setPage(1);
  };

  const tagOptions = useMemo(
    () => Array.from(new Set(animeRoles.flatMap((r) => r.tags))),
    []
  );
  const attrOptions = useMemo(
    () => Array.from(new Set(animeRoles.flatMap((r) => r.attributes))),
    []
  );
  const cvOptions = useMemo(
    () => Array.from(new Set(animeRoles.map((r) => r.cv))),
    []
  );

  const filtered = useMemo(() => {
    const q = keyword.trim().toLowerCase();
    const list = animeRoles.filter((r) => {
      const matchKeyword =
        !q ||
        r.name.toLowerCase().includes(q) ||
        r.alias.toLowerCase().includes(q) ||
        r.work.toLowerCase().includes(q);
      const matchTag = !tag || r.tags.includes(tag);
      const matchAttr = !attr || r.attributes.includes(attr);
      const matchCv = !cv || r.cv === cv;
      const matchNew = !onlyNew || r.isNew;
      return matchKeyword && matchTag && matchAttr && matchCv && matchNew;
    });
    return list.sort((a, b) =>
      sort === "popularity"
        ? b.popularity - a.popularity
        : a.firstAppear.localeCompare(b.firstAppear)
    );
  }, [keyword, tag, attr, cv, onlyNew, sort]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const current = Math.min(page, totalPages);
  const pageItems = filtered.slice((current - 1) * PAGE_SIZE, current * PAGE_SIZE);

  const handleReset = () => {
    setSearchParams(new URLSearchParams(), { replace: true });
    setPage(1);
  };

  return (
    <div className="mx-auto max-w-[1280px] px-4 py-6 md:px-6 md:py-8">
      <Breadcrumb items={[{ label: "首页", to: "/" }, { label: "角色列表" }]} />

      <div className="mt-4 mb-6">
        <h1 className="text-2xl text-foreground-950 md:text-3xl">萝莉角色分类</h1>
        <p className="mt-2 max-w-2xl text-sm leading-relaxed text-foreground-600">
          汇集二次元萝莉角色资料，支持按类型、属性、声优及是否新角色多维筛选，配合人气与发布时间排序，帮你快速定位心仪的角色。
        </p>
      </div>

      <FilterBar
        tagOptions={tagOptions}
        attrOptions={attrOptions}
        cvOptions={cvOptions}
        selectedTag={tag}
        selectedAttr={attr}
        selectedCv={cv}
        onlyNew={onlyNew}
        onSelectTag={(v) => update("tag", v)}
        onSelectAttr={(v) => update("attr", v)}
        onSelectCv={(v) => update("cv", v)}
        onToggleNew={() => update("new", onlyNew ? "" : "1")}
        onReset={handleReset}
      />

      <div className="mt-6 flex flex-wrap items-center justify-between gap-3">
        <p className="text-sm text-foreground-600">
          {keyword && (
            <span className="mr-2">
              关键词「<span className="text-primary-600">{keyword}</span>」 ·
            </span>
          )}
          共找到 <span className="font-medium text-foreground-900">{filtered.length}</span> 位角色
        </p>
        <div className="flex items-center gap-1 rounded-full border border-background-300 bg-background-50 p-1">
          <button
            onClick={() => setSort("popularity")}
            className={`rounded-full px-4 py-1.5 text-sm transition-colors cursor-pointer ${
              sort === "popularity"
                ? "bg-primary-500 text-background-50 dark:text-foreground-950"
                : "text-foreground-700 hover:text-primary-600"
            }`}
          >
            按人气
          </button>
          <button
            onClick={() => setSort("newest")}
            className={`rounded-full px-4 py-1.5 text-sm transition-colors cursor-pointer ${
              sort === "newest"
                ? "bg-primary-500 text-background-50 dark:text-foreground-950"
                : "text-foreground-700 hover:text-primary-600"
            }`}
          >
            按登场
          </button>
        </div>
      </div>

      <div className="mt-6">
        {pageItems.length > 0 ? (
          <div className="grid grid-cols-2 gap-4 md:grid-cols-3 lg:grid-cols-4">
            {pageItems.map((role) => (
              <RoleCard key={role.id} role={role} />
            ))}
          </div>
        ) : (
          <EmptyState
            icon="ri-emotion-sad-line"
            title="未找到符合条件的角色"
            desc="换个筛选条件试试，或者一键重置所有的筛选选项。"
            actionText="重置筛选"
            onAction={handleReset}
          />
        )}
      </div>

      {pageItems.length > 0 && (
        <div className="mt-10">
          <Pagination current={current} total={totalPages} onChange={setPage} />
        </div>
      )}
    </div>
  );
}